import { useState, useEffect } from 'react';
import { connect, RootStateOrAny } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { useLocation, useHistory } from "react-router-dom";
import moment from "moment";

import { Button, Alert, Checkbox, Modal, Typography, Table, Skeleton, Divider, Tag, Select, Input, Form } from "antd";
import { ExclamationCircleOutlined, PrinterOutlined } from '@ant-design/icons';

import { formatPhoneNumber, formatZipCode, handleDownload, getDisplayCompanyId } from 'utils/helpers';
import notifier from 'utils/notifier';
import { ErrorResponse, AuthModal, Attachment } from 'utils/types';
import { StatusCode, Color, UserRole, AuthAccess } from 'utils/enums';
import { diagRefCode, H5499_POS_CODES, ALLOWED_VENDOR_FACILITIES_FOR_AUTH_MODIFICATION, EXPRESS_RX_PROVIDER_ID } from 'utils/objects';
import { groupByCptCodes } from 'utils/helpers';

import { getPortalAuth, AuthDiags } from 'store/authorization/portal/actions';
import { getAttachInfo, downloadAttachment } from 'store/authorization/attach/actions';
import { getLetterInfo } from 'store/authorization/letter/actions';
import { modifyAuth, cancelAuth, extendAuth } from 'store/authorization/extendAndModify/actions';
import { getDownload, getDownloadableResources } from 'store/app/actions';
import { downloadFile, getAllFilesByTags, getSubmittedFileNames, uploadFilestoAuth } from 'store/authorization/files/actions';
import { getAllowExtendAndModify } from 'store/authorization/extendAndModify/actions';
import { get as getRuleCodes } from 'store/authorization/ruleCodes/actions';
import { getAuthMember } from 'store/authorization/member/actions';
import { getProvider } from 'store/eligibility/actions';
import { getEzCapProviderDetails } from 'store/authorization/provider/actions';
import { getHelpCenterLinks } from 'store/app/helpCenter/actions';

import ModifyAuthDetailModal from './ModifyAuthDetailModal';
import CancellationReasonModal from './CancellationReasonModal';
import HelpCenterLink from 'components/Common/HelpCenterLink';


import { AuthDetailProps, DownloadableResources, LetterInfo } from './Proptypes';
import constants from 'constants/index';
import styles from "./AuthDetail.module.scss";

const { confirm } = Modal;
const { Title } = Typography;

const diagColumns = [
  {
    title: 'REFERENCE',
    key: 'ref',
    dataIndex: 'ref',
  },
  {
    title: 'DIAG CODE',
    key: 'diagCode',
    dataIndex: 'diagCode',
  },
  {
    title: 'DESCRIPTION',
    key: 'desc',
    dataIndex: 'desc',
  },
];

const cptColumns = [
  {
    title: 'CPT CODE',
    key: 'cpt',
    dataIndex: 'cpt',
  },
  {
    title: 'DESCRIPTION',
    key: 'desc',
    dataIndex: 'desc',
  },
  {
    title: 'MODIFIER',
    key: 'mod',
    dataIndex: 'mod',
    render: (_: string, record: any, __: number) => record.mod?.join(' ')
  },
  {
    title: 'DIAG REF',
    key: 'diagRef',
    dataIndex: 'diagRef',
  },
  {
    title: 'QUANTITY',
    key: 'quantity',
    dataIndex: 'quantity',
  },
];

const notesColumns = [
  {
    title: 'SUBJECT',
    key: 'subject',
    dataIndex: 'subject',
  },
  {
    title: 'NOTES',
    key: 'notes',
    dataIndex: 'notes',
    render: (text: string) => <div style={{ whiteSpace: 'pre-wrap' }}>{text?.split('\r\n').map(item => <>{item}<br /></>)}</div>
  },
];

const AuthDetail = (props: AuthDetailProps) => {
  const history = useHistory();
  const location = useLocation();
  const [authDetailState, setAuthDetailState] = useState({
    authNo: '',
    healthPlanAuthNumber: '',
    requestDate: '',
    referenceType: '',
    certificateType: '',
    actionDate: '',
    createdDate: '',
    expirationDate: '',
    retroDate: '',
    companyId: '',
    status: '',
    statusNumber: '',
    memberName: '',
    memberPhoneNumber: '',
    healthPlan: '',
    dateOfBirth: '',
    gender: '',
    memberId: '',
    memberPcp: '',
    referralBy: '',
    referralByNumber: '',
    referralTo: '',
    referralSpecialty: '',
    referralPos: '',
    referralByFaxNumber: '',
    memberAddress: '',
    memberCity: '',
    memberState: '',
    memberZipCode: '',
    referralToAddress: '',
    referralToCity: '',
    referralToState: '',
    referralToZipCode: '',
    referralToPhoneNumber: '',
    referralToFaxNumber: '',
    facilityName: '',
    facilityAddress: '',
    facilityCity: '',
    facilityState: '',
    facilityZipCode: '',
    facilityPhoneNumber: '',
    authDiagsData: [{}],
    authDetailsData: [{}],
    authNotesData: [{}],
    formColumnData: [{}],
    letterInfos: [{}],
    showNotes: false,
    attachments: [{}],
  } as AuthModal);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [extendDisabled, setExtendDisabled] = useState(false);
  const [modifyDisabled, setModifyDisabled] = useState(false);
  const [cancelDisabled, setCancelDisabled] = useState(false);
  const [allowModify, setAllowModify] = useState(false);
  const [jCodes, setJCodes] = useState<any[]>([]);
  const [helpCenter, setHelpCenter] = useState<any>([]);
  const [downloadableForms, setDownloadableForms] = useState<any[]>([]);
  const [rXFormVisible, setRXFormVisible] = useState(false);
  const [openAuthDenyModal, setOpenAuthDenyModal] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const [showHospitalDates, setShowHospitalDates] = useState(false);

  const [referByPhoneNumber, setReferByPhoneNumber] = useState('');
  const [referByFaxNumber, setReferByFaxNumber] = useState('');
  const [isCancellationModalVisible, setIsCancellationModalVisible] = useState(false);
  const [authAccess, setAuthAccess] = useState('');
  useEffect(() => {
    getHelpCenterLinks();
    window.addEventListener("popstate", backEvent);
    return () => window.removeEventListener("popstate", backEvent);
  }, []);

  useEffect(() => {
    props.getPortalAuth(props.authDetail.authNo, props.authDetail.companyId, parseInt(sessionStorage.getItem('user_role')!))
    .then((data: any) => {
        props.getEzCapProviderDetails(data.RmiAuthPcpKeyId, data.RmiCompanyId)
        .then((data: any) => {
          setReferByPhoneNumber(data.provider?.phone);
          setReferByFaxNumber(data.provider?.fax);
        })
        .catch((err: ErrorResponse) => {
          notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        });

        setAllowExtendModifyAndCancel(data);

        const authDiagsData: Array<any> = [];
        const authDetailsData: Array<any> = [];
        const authNotesData: Array<any> = [];

        //need to split this into array because the API doesn't provide first and last name for these fields
        const memberFullName = data.RmiPcpRevFullName.split(',');
        const authPcpFullName = data.RmiPcpRevFullName.split(',');
        const requestProviderFullName = data.RmiReqRevFullName.split(',');

        // MedicoRX check
        if (data.RmiReqRevFullName && data.RmiReqRevFullName.toLowerCase().includes('medicorx specialty') && ['ADV', 'AHC', 'APC', 'GOM', 'AVISTA', 'CVIPA', 'BAIPA', 'GSGP'].includes(props.authDetail.companyId)) {
          setRXFormVisible(true);
        } else setRXFormVisible(false);

        //From the legacy code, it shows there are 6 different dialog types that are mixed inside AuthDiags response
        //TODO - need to rewrite to just use the Rmi API formats in the future
        for(const key in data.AuthDiags) {
          if(data.AuthDiags[key].RmiDiag !== 'RR' && data.AuthDiags[key].RmiDiag !== 'MR' && data.AuthDiags[key].RmiDiag !== 'RA' && data.AuthDiags[key].RmiDiag !== 'DR' && data.AuthDiags[key].RmiDiag !== 'UR' && data.AuthDiags[key].RmiDiag !== 'PR') {
            authDiagsData.push({
              key: key,
              ref: data.AuthDiags[key].RmiDiagRefNo,
              diagCode: data.AuthDiags[key].RmiDiag,
              desc: data.AuthDiags[key].RmiDiagDesc,
              RmiEffectDate: data.AuthDiags[key].RmiDiagFromDate,
              RmiSpecificity: data.AuthDiags[key].RmiSpecificity
            });
          }
        }

        //GetPortalAuth() api (data.AuthDetails) returns an array of (repeated cpt codes) objects with one modifier attached to them,
        //so the front-end needs to group the modifiers for the cpt code table...
        //New: If modifier is empty and has cptcode, that should be on a different row that is coming from UM portal
        //TODO - back-end should group them for the front-end like the claims api

        Object.values(groupByCptCodes(data.AuthDetails)).forEach((cptCode: any) => {
          if (cptCode.length > 0) {
            authDetailsData.push({
              cpt: cptCode[0].RmiProcCode,
              desc: cptCode[0].RmiDescr,
              mod: cptCode.map((x: any) => (x.RmiModif)),
              diagRef: cptCode[0].RmiDiagCode,
              quantity: cptCode[0].RmiQty,
              RmiPhCode: cptCode[0].RmiPhCode,
              RmiDiagCode: cptCode[0].RmiDiagCode // TODO - rewrite auth to use RmiDiagCode
            });
          } else {
            authDetailsData.push({
              cpt: cptCode[0].RmiProcCode,
              desc: cptCode[0].RmiDescr,
              mod: cptCode[0].RmiModif,
              diagRef: cptCode[0].RmiDiagCode,
              quantity: cptCode[0].RmiQty,
              RmiPhCode: cptCode[0].RmiPhCode,
              RmiDiagCode: cptCode[0].RmiDiagCode // TODO - rewrite auth to use RmiDiagCode
            });
          }
        });

        data.AuthNotes.filter((note: any) => note.RmiSubject !== 'LETTER ADDRESS').map((note: any, index: number) => {
            authNotesData.push({
              key: index,
              subject: note.RmiSubject,
              notes: note.RmiNotes
            });
        }) // DO NOT include Letter Address

        checkHospitalDates(data);

        setAuthDetailState((previousState: AuthModal) => ({
          ...previousState,
          authNo: data.RmiAuthNo,
          healthPlanAuthNumber: data.RmiHpAuthNo,
          requestDate: data.RmiReqDate,
          referenceType: data.AuthDiags.find((item: AuthDiags) => diagRefCode.includes(item.RmiDiag))?.RmiDiagDesc,
          certificateType: data.RmiCertTypeDesc,
          actionDate: data.RmiAuthDate,
          createdDate: data.RmiCreatedDate,
          expirationDate: data.RmiExprDate,
          retroDate: data?.RmiRetroDate ? data.RmiRetroDate : data.RmiAuthDate,
          companyId: data.RmiCompanyId,
          status: StatusCode[data.RmiStatus].toUpperCase(),
          statusNumber: data.RmiStatus,
          memberName: data.RmiMembName,
          memberPhoneNumber: formatPhoneNumber(data.RmiMembaddrPhone),
          healthPlan: data.RmiHpName,
          dateOfBirth: data.RmiBirth,
          gender: data.RmiSex,
          memberId: data.RmiMembId,
          membKeyId: data.RmiMembKeyId,
          memberPcp: data.RmiPcpRevFullName,
          primPcpKeyId: data.RmiPrimPcpKeyId,
          referralBy: data.RmiRefRevFullName,
          referralByNumber: formatPhoneNumber(data.RmiReferByPhone),
          referralTo: data.RmiReqRevFullName,
          referralSpecialty: data.RmiReqSpec,
          referralPos: data.RmiPlaceSvc,
          referralByFaxNumber: formatPhoneNumber(data.RmiReferByFax),
          memberAddress: data.RmiMembaddrStreet,
          memberCity: data.RmiMembaddrCity,
          memberState: data.RmiMembaddrState,
          memberZipCode: data.RmiMembaddrZip,
          referralToAddress: data.RmiSpecaddrStreet,
          referralToCity: data.RmiSpecaddrCity,
          referralToState: data.RmiSpecAddrState,
          referralToZipCode: data.RmiSpecaddrZip,
          referralToPhoneNumber: formatPhoneNumber(data.RmiSpecaddrPhone),
          referralToFaxNumber: formatPhoneNumber(data.RmiSpecaddrFax),
          facilityName: data.FANAME,
          facilityAddress: data.FASTREET,
          facilityCity: data.FACITY,
          facilityState: data.FASTATE,
          facilityZipCode: data.FAZIP,
          facilityPhoneNumber: formatPhoneNumber(data.FAPHONE),
          authDiagsData: authDiagsData,
          authDetailsData: authDetailsData,
          authNotesData: authNotesData,
          authPcpId: data.RmiAuthPcp,
          authPcpKeyId: data.RmiAuthPcpKeyId,
          requestProv: data.RmiReqProv,
          reqProvKeyId: data.RmiReqProvKeyId,
          authPcpFirstName: authPcpFullName[1],
          authPcpLastName: authPcpFullName[0],
          requestProviderFirstName: requestProviderFullName[1],
          requestProviderLastName: requestProviderFullName[0],
          memberFirstName: memberFullName[1],
          memberLastName: memberFullName[0],
          priorityStatus: data.RmiPriorityStatus,
          pos: data.RmiPOS,
          rmiAdmitDate: data.RmiAdmitDate,
          rmiDschDate: data.RmiDschDate,
          rmiDischDisp: data.rmiDischDisp
        }));

        const authAccess = props.providerList?.find((r: any) => r.RmiRevFullName === data.RmiPcpRevFullName);
        setAuthAccess(authAccess.RmiAuthAccess);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });

    props.getLetterInfo(props.authDetail.authNo, props.authDetail.companyId)
      .then((info: LetterInfo) => {
        const letterInfosArray: Array<Object> = [];
        for (const key in info) {
          if (info[key]['RmiFileName'].indexOf('APPROVAL_LETTER') >= 0 && info[key]['RmiFileName'].indexOf('PROVIDER') === -1)  {
            continue;
          }

          letterInfosArray.push({
            key: key,
            fileName: info[key]['RmiFileName'],
            fileLocation: info[key]['RmiFileLocation'],
            fileDate: info[key].RmiFileDate
          });
        }

        setAuthDetailState((previousState: AuthModal) => ({
          ...previousState,
          letterInfos: letterInfosArray
        }));

      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });

    props.getDownloadableResources('Form', 'Auth', props.authDetail.companyId)
      .then((formData: DownloadableResources[]) => {
        setDownloadableForms(formData);
      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });

    props.getAttachInfo(props.authDetail.authNo, props.authDetail.companyId)
      .then((attachmentData: Attachment) => {
        const attachmentArray: any = [];
        for (const key in attachmentData) {
          attachmentArray.push({
            key: key,
            authNo: attachmentData[key][0],
            companyId: attachmentData[key][1],
            folderLocation: attachmentData[key][2],
            fileName: attachmentData[key][3],
            fileMeta: attachmentData[key][4],
          });
        }
        setAuthDetailState( (previous: any) => ({ ...previous, attachments: attachmentArray }) );
      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });

    getRuleCodes('JCODE%20FORM%20CPT', setJCodes);

    // Feature flag for QA
    !!!constants.IS_PROD && getAYCEFileList();

  }, []);

  const backEvent = (e: any) => {
    if (e.state === null) {
      e.preventDefault();
      return false;
    }

    history.push(location.pathname);
    return props.triggerStartPage();
  }

  const getHelpCenterLinks = () => {
    props.getHelpCenterLinks(sessionStorage.getItem('ipaGroup')!, '/auth/search/detail', 'page')
      .then((response: any) => {
        setHelpCenter(response);
    })
    .catch((err: ErrorResponse) => {
      console.log(err?.response?.data?.Message);
    });
  }

  const getRuleCodes = (setName: string, setFunc: Function) => {
    return props.getRuleCodes(setName)
      .then((response: string[]) => {
        setFunc(response);
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const getAYCEFileList = () => {
    const tags = [
      {
        name: 'ipa',
        value: props.authDetail.companyId && props.authDetail.companyId.toLowerCase()
        // TEST VALUE
        // value: 'apc'
      },
      {
        name: 'authno',
        value: props.authDetail?.authNo
        // TEST VALUE
        // value: '123'
      }
    ];

    return props.getAllFilesByTags(props.authDetail.companyId, tags)
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const downloadAYCEFile = (filepath: string) => {
    return props.downloadFile(props.authDetail.companyId, filepath)
      .then((response: string) => {
        let blob = new Blob([response], {type: "application/pdf"});
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "_blank", "");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err?.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const setAllowExtendModifyAndCancel = (portalData: any) => {

    const today = new Date();
    const expiryDate = new Date(portalData.RmiExprDate);
    const reqDate = new Date(portalData.RmiReqDate);
    const termDate = new Date(portalData.RmiReqTermDate);

    const beforeExpiryDate = new Date(expiryDate.valueOf());
    beforeExpiryDate.setDate(expiryDate.getDate() - 15);
    const afterExpiryDate  = new Date(expiryDate.valueOf());
    afterExpiryDate.setDate(expiryDate.getDate() + 15);

    const acceptedBeforeRequestDate = new Date(reqDate.valueOf());//03-01-23
    acceptedBeforeRequestDate.setDate(acceptedBeforeRequestDate.getDate() + 15);
    const acceptedAfterRequestDate = new Date(reqDate.valueOf());//05-30-23
    acceptedAfterRequestDate.setDate(acceptedAfterRequestDate.getDate() + 105);

    const providerKeys = props.providerList.map((r: any) => r.PKey );

    if (expiryDate < today) {
      setAuthExpired(true);
    }

    if (portalData.RmiStatus === '1') { // APPROVED only

      // if (today < 15 days after Request date)
      if (today < acceptedBeforeRequestDate) {
        setExtendDisabled(true);
      }

      // if (today > 90 days after activation, 105 days after Request date)
      if (today > acceptedAfterRequestDate) {
        setExtendDisabled(true);
      }
    } else { // Other Status Codes

      // if (today < beforeExpiryDate) {
      if (today < beforeExpiryDate) {
          setExtendDisabled(true);
      }

      // if (today > afterExpiryDate) {
      if (today > afterExpiryDate) {
        setExtendDisabled(true);
      }
    }

    if (today > afterExpiryDate) {
      setModifyDisabled(true);
    }

    if ((!!portalData.RmiHpAuthNo || portalData.RmiAcct === "0050" || portalData.RmiAcct === "0056" || portalData.RmiAcct === "0060") &&
       (portalData.RmiStatus === "1" || portalData.RmiStatus === "4" || portalData.RmiStatus === "8")) {
      setExtendDisabled(true);
      setModifyDisabled(true);
    }

    if (portalData.RmiStatus === "2" || portalData.RmiStatus === "3" || portalData.RmiStatus === "4" || portalData.RmiStatus === "6") {
      setExtendDisabled(true);
      setModifyDisabled(true);
    }

    if (portalData.RmiReqContract == "CT" || portalData.RmiReqContract == "CN" || portalData.RmiReqContract == "N" ||
       (!!portalData.RmiReqTermDate && today> termDate)) {
      setExtendDisabled(true);
    }

    if (providerKeys.indexOf(portalData.RmiAuthPcpKeyId + '__' + portalData.RmiCompanyId) >= 0 || providerKeys.indexOf(portalData.RmiReqProvKeyId + '__' + portalData.RmiCompanyId) >= 0) {
      setAllowModify(true);
    }

    if (ALLOWED_VENDOR_FACILITIES_FOR_AUTH_MODIFICATION.includes(portalData.FAVENDORID)) {
      setAllowModify(true);
    }

    if (providerKeys.indexOf(portalData.RmiAuthPcpKeyId + '__' + portalData.RmiCompanyId) < 0 || ['1', '2', '3', '5', '6'].includes(portalData.RmiStatus))
      setCancelDisabled(true);

    props.getAllowExtendAndModify(props.authDetail.companyId)
      .then((response: any[]) => {
        response.map(value => {
          if (value.Parameter === 'AuthExtension' && value.ParaValue === 'N') setExtendDisabled(true);
          if (value.Parameter === 'AuthModification' && value.ParaValue ==='N') setModifyDisabled(true);
        });
      }).catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const isModalVisibleHandler = () => {
    setIsModalVisible(false);
  };

  const onPrint = () => {
    window.print();
  };

  const onShowNotes = () => {
    setAuthDetailState((previous: any) => ({...previous, showNotes: sessionStorage.getItem('user_role') === UserRole.HEALTH_PLAN ? false : !authDetailState.showNotes}));
  }

  const extendAuth = () => {
    const form_data = {
      "AuthNo": authDetailState.authNo,
      "CompanyId": authDetailState.companyId,
      "UserModel": {
        "UserId": sessionStorage.user_id
      }
    };

    confirm({
      title: 'Extend Auth',
      content: 'Are you sure you want to extend this auth?',
      icon: <ExclamationCircleOutlined />,
      onOk() {
        props.extendAuth(form_data).then((response: string) => {
          var newExpiryDate;
          var deniedMsg = "The request for extension has been denied."
          var allowed = ((authDetailState.companyId == "ALPHA" && authDetailState.requestProv == EXPRESS_RX_PROVIDER_ID) || (authDetailState.companyId == "LSMA")) ? 1: 2;
          if (response.substring(0, 8) == "Accepted") {
            newExpiryDate = response.substring(10);
            Modal.success({
              content: <>Your extension request has been submitted and the new extended date is {newExpiryDate}. <br/>Please allow 10-15 mins for the extension date to update.</>
            });
          }
          else {
            switch (response) {
              case "Unavailable":
                Modal.error({
                  content: <>{deniedMsg} </>,
                });
                break;

              case "MemberTermed":
                Modal.error({
                  content: <>{deniedMsg} <br /> This authorization does not meet the requirements for an automatic extension because
                  this member has already termed.</>,
                });
                break;

              case "InvalidDate":
                Modal.error({
                  content: <>{deniedMsg} <br /> This authorization does not meet the requirements for an automatic extension because
                  its expiration date does not fall in between the past 29 days and the next 14 days.</>,
                });
                break;

              case "InvalidAuth":
                Modal.error({
                  content: <>{deniedMsg} <br /> This authorization does not meet the requirements for an automatic extension because
                  of ONE of the following reasons: <br />
                  ** services were not rendered at a provider's office, <br />
                  ** OR this authorization was not previously approved, <br />
                  ** OR the provider has been termed.</>,
                });
                break;

              case "RetroAuth":
                Modal.error({
                  content: <>{deniedMsg} <br /> This authorization does not meet the requirements for an automatic extension because
                  retro authorizations cannot be automatically approved.</>,
                });
                break;

              case "ExtensionCountExceeded":
                Modal.error({
                  content: <>{deniedMsg} <br /> This authorization does not meet the requirements for an automatic extension because
                  it had already received {allowed} prior extension and has reached the limit.</>,
                });
                break;

              case "UnauthorizedProvider":
                Modal.error({
                  content: <>{deniedMsg} <br /> This authorization does not meet the requirements for an automatic extension because
                  this provider does not have privileges to alter this authorization.</>,
                });
                break;

            }
          }
        }).catch((err: ErrorResponse) => {
          Modal.error({
            content: <>The request for extension has been denied.<br />
                      This authorization does not meet the requirements for an automatic extension because
                      this provider does not have privileges to alter this authorization.</>,
          });
        });
      },
      onCancel() {},
    });
  }

  const modifyAuth = () => {
    if (allowModify) {
      if (authExpired) {
        setOpenAuthDenyModal(true);
        return;
      }
      setIsModalVisible(true);

    } else {
      notifier.error("Access denied!", "Only " + authDetailState.referralTo + " or " + authDetailState.referralBy + " may request modifications to this authorization");
    }
  }

  const isCancellationModalVisibleHandler = () => {
    setIsCancellationModalVisible(false);
  };

  const cancelAuth = () => {
    confirm({
      title: 'Cancel Auth',
      content: 'Are you sure you want to cancel this auth?',
      icon: <ExclamationCircleOutlined />,
      onOk() {
        setIsCancellationModalVisible(true);
      },
      onCancel() {
       },
    });
  };

  const downloadPDF = (filePath: string) => {
    props.getDownload(filePath)
      .then((response: string) => {
        let blob = new Blob([response], {type: "application/pdf"});
        const fileURL = URL.createObjectURL(blob);
        window.open(fileURL, "_blank", "");
      })
      .catch((err: ErrorResponse) => {
        notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
      });
  };

  const downloadAttachment = (fileInfo: any) => {
    const downloadAttachmentParams = {
      AuthNo: fileInfo.authNo,
      CompanyId: fileInfo.companyId,
      FileLocation: fileInfo.fileLocation,
      FileName: fileInfo.fileName
    };
    props.downloadAttachment(downloadAttachmentParams)
    .then((response: any) => {
      handleDownload(response, fileInfo.fileMeta);
    })
    .catch((err: ErrorResponse) => {
      notifier.error(err.response.data.Message || 'Something went wrong, please contact a system administrator.');
    });
  };

  const checkHospitalDates = (authData: any) => {
    let H5499Found = false;
    if (authData.RmiPOS && H5499_POS_CODES.includes(authData.RmiPOS.trim())) {
      H5499Found = authData.AuthDetails.some((detail: any) => detail.RmiProcCode && detail.RmiProcCode.trim() === "H5499");

        //03/17/17 changed spec per meeting with CM, UM, and Dr. Wang
        if (authData.RmiAuthDate && !H5499Found) {
            setShowHospitalDates(false);
        }
        else if (H5499Found && authData.RmiSvcType !== "65") {
            setShowHospitalDates(true);
            authData.AuthDetails.forEach((detail: any) => {
              if (detail.RmiProcCode != null) {
                var procCode = detail.RmiProcCode;
                //get admit and discharge date from detail line
                if (procCode.trim() === "H5499") {
                  authData.RmiAdmitDate = detail.RmiAdmitDate;
                  authData.RmiDschDate = detail.RmiDschDate;
                  authData.RmiDischDisp = authData.RmiDischDisp;
                }
              }
            })
        }
    } else {
        setShowHospitalDates(false);
    }
  };

  return (
    <>
    {
      helpCenter?.length > 0 ? helpCenter.map((hc: any) => {
        return (
          <>
            <div className="row">
              <div className="col-md-24">
                <HelpCenterLink
                  clickEventId={hc.ClickEventId}
                  linkName={hc.LinkName}
                  pagePath={hc.PagePath}
                  url={hc.Link}
                  companyId={hc.CompanyId}
                />
              </div>
            </div>
          </>
        );
      }) : null
    }

    {props.isFetchingPortalAuth ? (
      <>
       <Skeleton
          active
          paragraph={{ rows: 4 }}
       />
       <br />
       <Skeleton
          avatar
          active
          paragraph={{ rows: 8 }}
       />
       <br />
       <Skeleton
          active
          paragraph={{ rows: 4 }}
       />
      </>
      ) : (
        <div className={styles['print-auth-info']}>
          <Title className={`${styles['print-center']} ${styles['page-title']}`} level={4}>Authorization Details</Title>
          <div className={`col-lg-24 mb-1 ${styles['no-print']}`}>
            <div className="btn-wrapper">
              <Button type="primary" className={styles['full-width-button']} onClick={extendAuth} disabled={extendDisabled || authAccess === AuthAccess.READONLY } loading={props.isFetchingAllowExtendAndModify && props.isFetchingPortalAuth}>
                Extend Auth
              </Button>
              &nbsp;
              <Button type="primary" className={styles['full-width-button']} onClick={modifyAuth} disabled={modifyDisabled || authAccess === AuthAccess.READONLY } loading={props.isFetchingAllowExtendAndModify && props.isFetchingPortalAuth}>
                Request Modification To Auth
              </Button>
              &nbsp;
              <Button type="primary" className={styles['full-width-button']} onClick={cancelAuth} disabled={cancelDisabled || authAccess === AuthAccess.READONLY } loading={props.isFetchingAllowExtendAndModify && props.isFetchingPortalAuth}>
                Cancel Auth
              </Button>
              &nbsp;
              <Button type="primary" className={styles['full-width-button']} onClick={onPrint}>
                <PrinterOutlined /> Print
              </Button>
            </div>
          </div>

          <div className={`d-flex justify-content-end ${styles['no-print']}`}>
            <Checkbox
              onClick={() => onShowNotes()}
            />&nbsp;&nbsp;<span>Check to show and include "Symptoms and Treatments" in printout</span>
          </div>

          <div className={`row mt-3 ${styles['print-section']}`}>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              <table className={`${styles['test']}`} style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={1}>
                    <th>Auth Number:</th>
                    <td>{getDisplayCompanyId(authDetailState.companyId)} - {authDetailState.authNo}</td>
                  </tr>
                  <tr key={2}>
                    <th>Health Plan Auth No:</th>
                    <td>{authDetailState.healthPlanAuthNumber}</td>
                  </tr>
                  {showHospitalDates ? (
                    <>
                      <tr key={3}>
                        <th>Admit Date:</th>
                        <td>{authDetailState.rmiAdmitDate ? moment(authDetailState.rmiAdmitDate).format('MM/DD/YYYY'): ''}</td>
                      </tr>
                    </>
                  ) :
                  (
                    <>
                      <tr key={3}>
                        <th>Request Date:</th>
                        <td>{authDetailState.requestDate ? moment(authDetailState.requestDate).format('MM/DD/YYYY') : ''}</td>
                      </tr>
                      <tr key={4}>
                        <th>Referral Type:</th>
                        <td>{authDetailState.referenceType}</td>
                      </tr>
                      <tr key={5}>
                        <th>Cert Type:</th>
                        <td>{authDetailState.certificateType}</td>
                      </tr>

                    </>
                  )}
                </tbody>
              </table>
            </div>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              <table className={`${styles['test']}`} style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={6}>
                    <th>Status:</th>
                    <td>
                      { authDetailState.status === 'APPROVED' ? <Tag color={Color.APPROVED}>APPROVED</Tag> : null }
                      { authDetailState.status === 'MODIFIED' ? <Tag color={Color.MODIFIED}>MODIFIED</Tag> : null }
                      { authDetailState.status === 'DEFERRED' ? <Tag color={Color.DEFERRED}>DEFERRED</Tag> : null }
                      { authDetailState.status === 'CANCELLED' ? <Tag color={Color.CANCELLED}>CANCELLED</Tag> : null }
                      { authDetailState.status === 'EXPIRED' ? <Tag color={Color.EXPIRED}>EXPIRED</Tag> : null }
                      { authDetailState.status === 'DENIED' ? <Tag color={Color.DENIED}>DENIED</Tag> : null }
                      { authDetailState.status === 'PENDED' ? <Tag color={Color.PENDED}>PENDED</Tag> : null }
                      { authDetailState.status === 'REQUESTED' ? <Tag style={{ "color": Color.REQUESTED_TEXT}}  color={Color.REQUESTED}>REQUESTED</Tag> : null }
                      { authDetailState.status === 'SYSTEM_HOLD' ? <Tag style={{ "color": Color.SYSTEM_HOLD_TEXT}} color={Color.SYSTEM_HOLD}>SYSTEM HOLD</Tag> : null }
                    </td>
                  </tr>
                  {showHospitalDates ? (
                    <>
                      <tr key={7}>
                        <th>Discharge Date:</th>
                        <td>{authDetailState.rmiDschDate ? moment(authDetailState.rmiDschDate).format('MM/DD/YYYY') : ''}</td>
                      </tr>
                      <tr key={8}>
                        <th>Discharge Disposition:</th>
                        <td>{authDetailState.rmiDischDisp}</td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr key={7}>
                        <th>Action Date:</th>
                        <td>{moment(authDetailState.actionDate).format('MM/DD/YYYY')}</td>
                      </tr>
                      <tr key={8}>
                        <th>Expiration Date:</th>
                        <td>{authDetailState.expirationDate ? moment(authDetailState.expirationDate).format('MM/DD/YYYY') : ''}</td>
                      </tr>
                      <tr key={9}>
                        <th className={styles['bold']}>Retro Date:</th>
                        {authDetailState.referenceType === 'RETROACTIVE REFERRAL' && (
                          <td>{moment(authDetailState.retroDate).isValid() && moment(authDetailState.retroDate).format('MM/DD/YYYY')}</td>
                        )}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`}/>

          <div className={`row ${styles['print-section']}`}>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              <table className={`${styles['test']}`}  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={10}>
                    <th>Patient Name:</th>
                    <td>
                      {authDetailState.memberName}
                      <br />
                      {authDetailState.memberAddress} <br/>
                      {authDetailState.memberCity}, &nbsp; {authDetailState.memberState} &nbsp; {formatZipCode(authDetailState.memberZipCode)}
                    </td>
                  </tr>
                  <tr key={11}>
                    <th className={styles['bold']}>Phone:</th>
                    <td>{authDetailState.memberPhoneNumber}</td>
                  </tr>
                  <tr key={12}>
                    <th className={styles['bold']}>Health Plan:</th>
                    <td>{authDetailState.healthPlan}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={`col-lg-12 col-print-6 ${styles['print-section']}`}>
              <table className={`${styles['test']}`}  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={13}>
                    <th>Date of Birth:</th>
                    <td>
                      {moment(authDetailState.dateOfBirth).format('MM/DD/YYYY')}
                    </td>
                  </tr>
                  <tr key={14}>
                    <th>Gender: </th>
                    <td>{authDetailState.gender}</td>
                  </tr>
                  <tr key={15}>
                    <th className={styles['bold']}>Member ID:</th>
                    <td>{authDetailState.memberId}</td>
                  </tr>
                  <tr key={16}>
                    <th>Member PCP:</th>
                    <td>{authDetailState.memberPcp}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`} />

          <div className="row">
            <div className={`col-lg-12 col-print-6 ${styles['print-section']}`}>
              <table className={`${styles['test']}`}  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={17}>
                    <th className={styles['bold']}>Referral By:</th>
                    <td>{authDetailState.referralBy}</td>
                  </tr>
                  <tr key={18}>
                    <th className={styles['bold']}>Phone Number:</th>
                    <td>{referByPhoneNumber ? formatPhoneNumber(referByPhoneNumber) : authDetailState.referralByNumber}</td>
                  </tr>
                  <tr key={19}>
                    <th className={styles['bold']}>Fax Number:</th>
                    <td>{referByFaxNumber ? formatPhoneNumber(referByFaxNumber) : authDetailState.referralByFaxNumber}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`} />

          <div className="row">
            <div className={`col-lg-12 col-print-6 ${styles['print-section']}`}>
              <table className={`${styles['test']}`}  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={20}>
                    <th>Referral To:</th>
                    <td>{authDetailState.referralTo}</td>
                  </tr>
                  <tr key={21}>
                    <th>Specialty:</th>
                    <td>{authDetailState.referralSpecialty}</td>
                  </tr>
                  <tr key={22}>
                    <th>POS:</th>
                    <td>{authDetailState.referralPos}</td>
                  </tr>
                  <tr key={23}>
                    <th className={styles['bold']}>Facility:</th>
                    <td>{authDetailState.facilityName}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={`col-lg-12 ${styles['col-print-6']} ${styles['print-section']}`}>
              <table className={`${styles['test']}`}  style={{ textAlign: "left", tableLayout: 'fixed', width: '100%' }}>
                <tbody>
                  <tr key={10}>
                    <th className={styles['bold']}>Address:</th>
                    <td>
                      {authDetailState.referralToAddress} <br/>
                      {authDetailState.referralToCity ? authDetailState.referralToCity + ',' : ''} {authDetailState.referralToState} {formatZipCode(authDetailState.referralToZipCode)}
                    </td>
                  </tr>
                  <tr key={24}>
                    <th className={styles['bold']}>Phone Number:</th>
                    <td>{authDetailState.referralToPhoneNumber}</td>
                  </tr>
                  <tr key={25}>
                    <th className={styles['bold']}>Fax Number:</th>
                    <td>{authDetailState.referralToFaxNumber}</td>
                  </tr>
                  <tr key={26}>
                    <th className={styles['bold']}>Facility Address:</th>
                    <td>
                      {authDetailState.facilityAddress} <br/>
                      {authDetailState.facilityCity ? authDetailState.facilityCity + ',' : ''} {authDetailState.facilityState} {formatZipCode(authDetailState.facilityZipCode)}
                    </td>
                  </tr>
                  <tr key={27}>
                    <th className={styles['bold']}>Facility Phone Number:</th>
                    <td>{authDetailState.facilityPhoneNumber}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`} />

          <Table
            className={styles['diag-table']}
            columns={diagColumns}
            dataSource={authDetailState.authDiagsData}
            rowKey={'index'}
            pagination={false}
          />

          <br />

          <Table
            className={styles['cpt-table']}
            columns={cptColumns}
            dataSource={authDetailState.authDetailsData}
            rowKey={'RmiProcCode'}
            pagination={false}
          />

          {rXFormVisible ? (
          <>
            <div className={styles['no-print']}>
              {downloadableForms.map(val => {
                if (val.RmiFileDesc === 'MedicoRX Prescription Request Form') {
                  return <a href='#/' onClick={() => downloadPDF(val.RmiLocationPath + val.RmiFileName)}>Download {val.RmiFileDesc}</a>;
                }
              })}

            </div>

            <Divider className={`${styles['print-ant-divider-horizontal']}`}/>
          </>
          ) : (
            <br />
          )}

          {authDetailState.authDetailsData.find(x => x.cpt && jCodes.includes(x.cpt.toUpperCase().trim())) && (
            <>
              <div className={styles['no-print']}>
                {downloadableForms.map(val => {
                  if (val.RmiFileDesc === "Prescription Drug Prior Authorization Form") {
                    return <a href='#/' onClick={() => downloadPDF(val.RmiLocationPath + val.RmiFileName)}>Download Prescription Drug Form</a>;
                  }
                })}

              </div>

              <Divider className={`${styles['print-ant-divider-horizontal']}`}/>
            </>
          )}

          <div className="col-lg-24 mb-1">
            <label className={`${styles['label']}`}>Notes:</label>
            { authDetailState.showNotes ?
              <Table className={`${styles['notes']}`} columns={notesColumns} dataSource={authDetailState.authNotesData} pagination={false} /> : null
            }
          </div>

          <div className="col-lg-24 col-md-24">
            <div className="row">
              <div className="col-lg-6">
                <label className={`${styles['label']}`}>Attachment:</label>
                <ul className={styles['attachment']}>
                  { authDetailState.attachments.map((data: any, key: number) => {
                    return (
                      <li>
                        <Button
                          type="link"
                          className={`${styles['attachment-link']} pl-1`}
                          key={key}
                          onClick={() => downloadAttachment(data)}>{data.fileMeta}
                        </Button>
                      </li>
                    );
                  })}
                </ul>

                <label className={`${styles['label']}`}>Notifications:</label>
                <ul className={styles['letterInfo']}>
                  {authDetailState.letterInfos.map((data: any, key: number) => {
                    return (
                      <li>
                        <span>{moment(data.fileDate).format("MMM D, YYYY [at] h:mmA")}</span>&nbsp; -
                        <Button
                          type="link"
                          className={`${styles['letterInfo-link']} pl-1`}
                          key={key}
                          onClick={() => downloadPDF(data.fileLocation + '\\' + data.fileName)}>
                          {data.fileName}
                        </Button>
                      </li>
                    );
                  })}

                  {props.fileList.map((data: any, key: number) => {
                    return (
                      <li>
                        <span>{moment(data.fileDate).format("MMM D, YYYY [at] h:mmA")}</span>&nbsp; -
                        <Button
                          type="link"
                          className={`${styles['letterInfo-link']} pl-1`}
                          key={key}
                          onClick={() => downloadAYCEFile(data.name)}
                        >
                          {data.name}
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>

          <Divider className={`${styles['print-ant-divider-horizontal']}`}/>

          <Alert
            message="Provider&#8217;s Responsibilities"
            description={
            <>
              Eligibility must be verified 24 hours before the actual date of service.
              Provider MUST confirm with Health Plan/IPA that the member&#8217;s health plan coverage is still in effect. {getDisplayCompanyId(authDetailState.companyId)} reserves
              the right to revoke this authorization prior to services being rendered based on cancellation of the member&#8217;s eligibility.
              Final determination of benefits will be made after review of the claim for limitations or exclusions.
            </>
            }
            type="info"
            showIcon
          />

          <div className={`row ${styles['print-section']}`}>
            <div className={`col-lg-12 ${styles['col-print-6']}`}>
              REF#: {getDisplayCompanyId(authDetailState.companyId)}-{authDetailState.authNo}:{authDetailState.memberId}
            </div>

            <div className={`col-lg-12 d-flex justify-content-end ${styles['col-print-6']}`}>
              {moment().format("MM/DD/YYYY [at] h:mmA")}
            </div>
          </div>

          <ModifyAuthDetailModal
            authDetailState={authDetailState}
            isModalVisible={isModalVisible}
            user={sessionStorage.user_id}
            isModalVisibleHandler={isModalVisibleHandler}
            getSubmittedFileNames={props.getSubmittedFileNames}
            uploadFilestoAuth={props.uploadFilestoAuth}
            modifyAuth={props.modifyAuth}
            isModifyingAuth={props.isModifyingAuth}
            isUploading={props.isUploading}
            getAuthMember={props.getAuthMember}
            getProvider={props.getProvider}
            getPortalAuth={props.getPortalAuth}
          />

          <Modal
            className={styles['auth-deny-modal']}
            visible={openAuthDenyModal}
            closable={false}
            width={600}
            footer={null}
          >
            <Title className="text-center" level={3}>Access denied!</Title>
            <p style={{ textAlign: "center", fontSize: "14px" }}>
              Modification request can no longer be made as authorization has expired.
            </p>
            <Button
              className="green"
              style={{left: "44%"}}
              key="OK"
              type="primary"
              onClick={() => setOpenAuthDenyModal(false)}
              loading={false}
            >
              OK
            </Button>
          </Modal>

          <CancellationReasonModal
          setAuthDetailState={setAuthDetailState}
          setCancelDisabled={setCancelDisabled}
          isCancellationModalVisible={isCancellationModalVisible}
          setIsCancellationModalVisible= {setIsCancellationModalVisible}
          isCancellationModalVisibleHandler={isCancellationModalVisibleHandler}
          authNo = {authDetailState.authNo}
          companyId = {authDetailState.companyId}
          cancelAuth={props.cancelAuth}
          />
        </div>
      )}
    </>
  );
};

const mapStateToProps = (store: RootStateOrAny) => ({
  isModifyingAuth: store.rootReducer.authExtendAndModify.isModifying,
  isFetchingAllowExtendAndModify: store.rootReducer.authExtendAndModify.isFetching,
  isFetchingPortalAuth: store.rootReducer.authPortal.isFetching,
  isFetchingRuleCodes: store.rootReducer.authRuleCodes.isFetching,
  isUploading: store.rootReducer.authFiles.isUploading,
  isFetchingAllFilesByTags: store.rootReducer.authFiles.isFetchingAllFilesByTags,
  isDownloading: store.rootReducer.authFiles.isDownloading,
  fileList: store.rootReducer.authFiles.files,
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
  {
    getPortalAuth,
    getAttachInfo,
    downloadAttachment,
    getLetterInfo,
    modifyAuth,
    cancelAuth,
    extendAuth,
    getDownload,
    getDownloadableResources,
    getSubmittedFileNames,
    uploadFilestoAuth,
    getAllowExtendAndModify,
    getRuleCodes,
    getAuthMember,
    getProvider,
    getAllFilesByTags,
    downloadFile,
    getEzCapProviderDetails,
    getHelpCenterLinks,
  },
  dispatch,
);
export default connect(mapStateToProps, mapDispatchToProps)(AuthDetail);